import { useMemo, useState, type KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEngine } from '@/app/EngineContext';
import { useWindowStore } from '@/store/windowStore';
import { openApplication } from '@/features/windows/windowManager';
import { CommandDialog, CommandItem, EmptyRow } from '@/components/ui';

/**
 * Application launcher: a searchable, keyboard-driven list over the registry.
 * ⌘K / Ctrl+K toggles it; ↑/↓ move, Enter opens, Esc closes.
 */
export const Launcher = () => {
  const open = useWindowStore((s) => s.launcherOpen);
  return open ? <LauncherDialog /> : null;
};

const LauncherDialog = () => {
  const { applications } = useEngine();
  const close = () => {
    useWindowStore.getState().setLauncherOpen(false);
  };
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const results = useMemo(() => applications.search(query), [applications, query]);
  const clamped = Math.min(index, Math.max(0, results.length - 1));
  const activeId = results[clamped] ? `launcher-${results[clamped].id}` : undefined;

  const launch = (i: number) => {
    const app = results[i];
    if (!app) return;
    close();
    openApplication(app.id, applications);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    e.stopPropagation();
    const n = Math.max(1, results.length);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setIndex((clamped + 1) % n);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setIndex((clamped - 1 + n) % n);
        break;
      case 'Enter':
        e.preventDefault();
        launch(clamped);
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
    }
  };

  return (
    <CommandDialog
      label="Application launcher"
      testId="launcher"
      open
      onClose={close}
      onKeyDown={onKeyDown}
      query={query}
      onQueryChange={(v) => {
        setQuery(v);
        setIndex(0);
      }}
      placeholder="Search applications…"
      inputLabel="Search applications"
      inputTestId="launcher-search"
      listId="launcher-list"
      activeId={activeId}
      footer="↑↓ navigate · ↵ open · esc close"
    >
      {results.length === 0 && <EmptyRow>No applications match “{query}”.</EmptyRow>}
      {results.map((app, i) => (
        <CommandItem
          key={app.id}
          id={`launcher-${app.id}`}
          role="option"
          aria-selected={i === clamped}
          selected={i === clamped}
          data-testid="launcher-item"
          onPointerEnter={() => {
            setIndex(i);
          }}
          onClick={() => {
            launch(i);
          }}
          sx={{ gap: 2.5 }}
        >
          <Box
            aria-hidden
            sx={{
              width: 32,
              height: 32,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 1.5,
              bgcolor: 'action.hover',
              fontSize: 16,
            }}
          >
            {app.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontWeight: 600 }}>{app.name}</Typography>
            <Typography variant="body2" color="text.disabled" noWrap>
              {app.description}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.disabled">
            {app.category} · v{app.version}
          </Typography>
        </CommandItem>
      ))}
    </CommandDialog>
  );
};
